import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Plus, Edit, Trash2, Star, Copy, ArrowLeft, Upload, Download, FileText } from 'lucide-react'
import api from '../lib/api'

const TemplateManager = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const fileInputRef = useRef(null)
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'Custom',
    file: null
  })
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'General',
    executiveSummaryTemplate: '',
    sections: []
  })

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates')
      setTemplates(response.data)
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate._id}`, formData)
      } else {
        await api.post('/templates', formData)
      }
      setShowModal(false)
      setEditingTemplate(null)
      setFormData({
        name: '',
        description: '',
        category: 'General',
        executiveSummaryTemplate: '',
        sections: []
      })
      toast.success(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!')
      fetchTemplates()
    } catch (error) {
      console.error('Failed to save template:', error)
      toast.error('Failed to save template')
    }
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description,
      category: template.category,
      executiveSummaryTemplate: template.executiveSummaryTemplate,
      sections: template.sections
    })
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this template?')) return
    
    try {
      await api.delete(`/templates/${id}`)
      toast.success('Template deleted successfully')
      fetchTemplates()
    } catch (error) {
      console.error('Failed to delete template:', error)
      toast.error(error.response?.data?.error || 'Failed to delete template')
    }
  }

  const handleSetDefault = async (id) => {
    try {
      await api.post(`/templates/${id}/set-default`)
      toast.success('Default template updated')
      fetchTemplates()
    } catch (error) {
      console.error('Failed to set default:', error)
      toast.error('Failed to set default template')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setUploadData({ ...uploadData, file, name: uploadData.name || file.name.replace(/\.[^/.]+$/, '') })
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    if (!uploadData.file) {
      toast.error('Please select a file')
      return
    }
    if (!uploadData.name) {
      toast.error('Please enter a template name')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('templateFile', uploadData.file)
      formData.append('name', uploadData.name)
      formData.append('description', uploadData.description)
      formData.append('category', uploadData.category)

      const response = await api.post('/templates/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      toast.success(`Template created with ${response.data.sectionsExtracted} sections extracted!`)
      setShowUploadModal(false)
      setUploadData({ name: '', description: '', category: 'Custom', file: null })
      if (fileInputRef.current) fileInputRef.current.value = ''
      fetchTemplates()
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error(error.response?.data?.error || 'Failed to upload template')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (template) => {
    if (!template.uploadedFile) {
      toast.error('No file associated with this template')
      return
    }
    try {
      const response = await api.get(`/templates/${template._id}/download`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', template.uploadedFile.originalName || 'template.pdf')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Failed to download template file')
    }
  }

  const addSection = () => {
    setFormData({
      ...formData,
      sections: [
        ...formData.sections,
        { title: '', content: '', placeholder: '', order: formData.sections.length, required: false }
      ]
    })
  }

  const updateSection = (index, field, value) => {
    const newSections = [...formData.sections]
    newSections[index][field] = value
    setFormData({ ...formData, sections: newSections })
  }

  const removeSection = (index) => {
    const newSections = formData.sections.filter((_, i) => i !== index)
    setFormData({ ...formData, sections: newSections })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/proposals')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Proposal Templates</h1>
          <p className="text-gray-600 mt-1">Create and manage reusable proposal templates</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
        >
          <Upload className="w-5 h-5 mr-2" />
          Upload Template
        </button>
        <button
          onClick={() => {
            setEditingTemplate(null)
            setFormData({
              name: '',
              description: '',
              category: 'General',
              executiveSummaryTemplate: '',
              sections: []
            })
            setShowModal(true)
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Template
        </button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div
            key={template._id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  {template.isDefault && (
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  )}
                </div>
                <p className="text-sm text-gray-600">{template.category}</p>
              </div>
            </div>

            {template.description && (
              <p className="text-sm text-gray-700 mb-4 line-clamp-2">{template.description}</p>
            )}

            {template.uploadedFile && (
              <div className="flex items-center gap-2 text-xs text-blue-600 mb-3">
                <FileText className="w-3 h-3" />
                <span>{template.uploadedFile.originalName}</span>
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>{template.sections?.length || 0} sections</span>
              <span>Used {template.usageCount || 0} times</span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(template)}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
              {template.uploadedFile && (
                <button
                  onClick={() => handleDownload(template)}
                  className="px-3 py-2 text-sm border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50"
                  title="Download original file"
                >
                  <Download className="w-4 h-4" />
                </button>
              )}
              {!template.isDefault && (
                <>
                  <button
                    onClick={() => handleSetDefault(template._id)}
                    className="px-3 py-2 text-sm border border-yellow-300 text-yellow-600 rounded-lg hover:bg-yellow-50"
                    title="Set as default"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template._id)}
                    className="px-3 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500">No templates found. Create your first template!</p>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option>General</option>
                    <option>IT Services</option>
                    <option>Construction</option>
                    <option>Consulting</option>
                    <option>Product</option>
                    <option>Custom</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Executive Summary Template *</label>
                <textarea
                  value={formData.executiveSummaryTemplate}
                  onChange={(e) => setFormData({...formData, executiveSummaryTemplate: e.target.value})}
                  rows={3}
                  placeholder="Use {{variable}} for placeholders, e.g., {{organization}}, {{title}}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Sections</label>
                  <button
                    type="button"
                    onClick={addSection}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    + Add Section
                  </button>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {formData.sections.map((section, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSection(index, 'title', e.target.value)}
                          placeholder="Section Title"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                        <button
                          type="button"
                          onClick={() => removeSection(index)}
                          className="ml-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(index, 'content', e.target.value)}
                        placeholder="Section content template..."
                        rows={2}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    setEditingTemplate(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Template Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Upload Proposal Template</h3>
            <p className="text-sm text-gray-600 mb-4">
              Upload a PDF or DOC file containing your proposal format. The system will extract the structure and create a reusable template.
            </p>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template File *</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    id="template-file"
                  />
                  <label htmlFor="template-file" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    {uploadData.file ? (
                      <p className="text-sm text-primary-600 font-medium">{uploadData.file.name}</p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, or TXT (max 10MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={uploadData.name}
                  onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                  placeholder="e.g., Government Proposal Format"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={uploadData.category}
                  onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="General">General</option>
                  <option value="IT Services">IT Services</option>
                  <option value="Government">Government</option>
                  <option value="Construction">Construction</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Product">Product</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description of this template..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false)
                    setUploadData({ name: '', description: '', category: 'Custom', file: null })
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadData.file}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload & Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateManager
